import {
  EuiBadge,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHealth,
  EuiIcon,
  EuiPanel,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { OpsanioWaves } from "src/questionnaires";
import { useAnketaI18n } from "src/internationalization";
import { WaveSummary, waveStateColor } from "./model";

/** Translated label for the single wave-state vocabulary. */
export const useWaveStateLabel = (): ((
  summary: WaveSummary,
) => string) => {
  const { translate } = useAnketaI18n();
  return (summary: WaveSummary) => {
    switch (summary.state) {
      case "root":
        return translate("anketa.opsanio.waves.state.root", {
          defaultMessage: "Root · floating tags",
        });
      case "unconfig":
        return translate("anketa.opsanio.waves.state.unconfigured", {
          defaultMessage: "Not configured",
        });
      case "blocked":
        return translate("anketa.opsanio.waves.state.blocked", {
          defaultMessage: "Blocked",
        });
      case "inflight":
        return translate("anketa.opsanio.waves.state.inflight", {
          defaultMessage: "Update in flight",
        });
      case "updates":
        return translate("anketa.opsanio.waves.state.updates", {
          defaultMessage: "Updates available",
        });
      default:
        return translate("anketa.opsanio.waves.state.current", {
          defaultMessage: "Up to date",
        });
    }
  };
};

/**
 * The promotion chain as a vertical rail: one row per wave in promotion order
 * (dev → main → qa → wave1…3 → debug), selectable, with the whole chain visible
 * at a glance instead of seven full-height cards stacked in a column.
 */
export const WaveChainRail = ({
  summaries,
  selected,
  onSelect,
}: {
  summaries: WaveSummary[];
  selected: OpsanioWaves;
  onSelect: (wave: OpsanioWaves) => void;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const stateLabel = useWaveStateLabel();

  const metaFor = (s: WaveSummary): string => {
    const bits: string[] = [];
    bits.push(
      s.appliances.length === 0
        ? translate("anketa.opsanio.waves.appliances.none", {
            defaultMessage: "No appliances",
          })
        : translate("anketa.opsanio.waves.appliances.count", {
            values: { count: s.appliances.length },
            defaultMessage: "{{count}} appliances",
          }),
    );
    if (s.outdatedCount > 0) {
      bits.push(
        translate("anketa.opsanio.waves.components.outdated_count", {
          values: { count: s.outdatedCount },
          defaultMessage: "{{count}} outdated",
        }),
      );
    }
    if (s.merge?.iid) bits.push(`!${s.merge.iid}`);
    return bits.join(" · ");
  };

  return (
    <EuiPanel hasBorder paddingSize="none" hasShadow={false}>
      <EuiPanel color="subdued" paddingSize="s" hasShadow={false} borderRadius="none">
        <EuiText size="xs" color="subdued">
          <strong>
            {translate("anketa.opsanio.waves.chain", {
              defaultMessage: "Promotion chain",
            })}
          </strong>
        </EuiText>
      </EuiPanel>

      {summaries.map((s) => {
        const isSelected = s.wave === selected;
        return (
          <EuiPanel
            key={s.wave}
            paddingSize="s"
            hasShadow={false}
            hasBorder={false}
            borderRadius="none"
            color={isSelected ? "primary" : "transparent"}
            // A panel is a div, so the row carries its own button semantics
            // rather than `element="button"` - the row's content (flex groups,
            // a heading) is flow content and may not live inside a <button>.
            role="button"
            tabIndex={0}
            aria-current={isSelected ? "true" : undefined}
            onClick={() => onSelect(s.wave)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(s.wave);
              }
            }}
            style={{ cursor: "pointer" }}
          >
            <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
              <EuiFlexItem grow={false}>
                <EuiBadge color={isSelected ? "primary" : "hollow"}>
                  {s.stage}
                </EuiBadge>
              </EuiFlexItem>
              <EuiFlexItem style={{ minWidth: 0 }}>
                <EuiFlexGroup
                  gutterSize="s"
                  alignItems="center"
                  responsive={false}
                >
                  <EuiFlexItem grow={false}>
                    <EuiTitle size="xxs">
                      <h3 style={{ textTransform: "uppercase" }}>{s.wave}</h3>
                    </EuiTitle>
                  </EuiFlexItem>
                  <EuiFlexItem style={{ minWidth: 0 }}>
                    <EuiHealth
                      color={waveStateColor(s.state)}
                      textSize="xs"
                      className="eui-textTruncate"
                    >
                      {stateLabel(s)}
                    </EuiHealth>
                  </EuiFlexItem>
                </EuiFlexGroup>
                <EuiText size="xs" color="subdued" className="eui-textTruncate">
                  {metaFor(s)}
                </EuiText>
              </EuiFlexItem>
              {isSelected && (
                <EuiFlexItem grow={false}>
                  <EuiIcon type="arrowRight" size="s" color="primary" />
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
          </EuiPanel>
        );
      })}
    </EuiPanel>
  );
};
